import React, { useState, useEffect } from 'react';
import { IconNews, IconRefresh } from '@tabler/icons-react';

const NewsSidebar = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchNews = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch from cloud function
      const response = await fetch('https://us-central1-southern-list-468215-m7.cloudfunctions.net/getSportsNews');

      if (!response.ok) {
        throw new Error('Failed to fetch news');
      }

      const data = await response.json();

      if (data.success) {
        setNews(data.articles || []);
        setLastUpdated(data.lastUpdated);
      } else {
        throw new Error(data.error || 'Failed to load news');
      }
    } catch (err) {
      console.error('Error fetching sports news:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
  }, []);

  const formatTimeAgo = (dateString) => {
    if (!dateString) return '';

    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));

      if (diffInHours < 1) return 'Just now';
      if (diffInHours < 24) return `${diffInHours}h ago`;
      const diffInDays = Math.floor(diffInHours / 24);
      if (diffInDays === 1) return '1 day ago';
      return `${diffInDays} days ago`;
    } catch (e) {
      return '';
    }
  };

  return (
    <aside className="ub-news-sidebar">
      <div className="ub-news-header">
        <div className="ub-news-header-left">
          <IconNews size={24} stroke={2} />
          <h3>Sports Headlines</h3>
        </div>
        <button
          className="ub-refresh-button"
          onClick={fetchNews}
          disabled={loading}
          title="Refresh news"
        >
          <IconRefresh size={18} className={loading ? 'ub-rotating' : ''} />
        </button>
      </div>

      {loading && news.length === 0 && (
        <div className="ub-news-loading">
          <IconRefresh size={32} className="ub-rotating" />
          <p>Loading headlines...</p>
        </div>
      )}

      {error && !loading && (
        <div className="ub-news-error">
          <p>Unable to load headlines</p>
          <button className="ub-retry-button" onClick={fetchNews}>
            Try Again
          </button>
        </div>
      )}

      {!loading && news.length > 0 && (
        <div className="ub-news-list">
          {news.slice(0, 8).map((article, index) => (
            <a
              key={index}
              href={article.link}
              target="_blank"
              rel="noopener noreferrer"
              className="ub-news-item"
            >
              <div className="ub-news-item-content">
                <h4 className="ub-news-title">{article.title}</h4>
                {article.description && (
                  <p className="ub-news-description">
                    {article.description.substring(0, 100)}
                    {article.description.length > 100 ? '...' : ''}
                  </p>
                )}
                <div className="ub-news-meta">
                  <span className="ub-news-source">{article.source}</span>
                  {article.publishedDate && (
                    <>
                      <span className="ub-news-separator">•</span>
                      <span className="ub-news-time">{formatTimeAgo(article.publishedDate)}</span>
                    </>
                  )}
                </div>
              </div>
            </a>
          ))}
        </div>
      )}

      {lastUpdated && (
        <div className="ub-news-footer">
          <span className="ub-last-updated">
            Updated {formatTimeAgo(lastUpdated?.toDate ? lastUpdated.toDate() : lastUpdated)}
          </span>
        </div>
      )}
    </aside>
  );
};

export default NewsSidebar;
